
import React, { useState, useEffect } from 'react';
import VideoWall from '../components/VideoWall';
import { supabase } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { getAllVideos, getMerchantVideos, getUserVideos } from '@/services/videoService';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { RefreshCw, Store, User, Video } from 'lucide-react';

export default function VideoWallPage() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>('');
  const [videos, setVideos] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [videoType, setVideoType] = useState<'merchant' | 'user'>('merchant'); // Default to merchant videos
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
    loadVideos();
    
    // Set an interval to refresh videos every 30 seconds
    const refreshInterval = setInterval(() => {
      loadVideos();
    }, 30000);
    
    // Clean up interval on unmount
    return () => clearInterval(refreshInterval);
  }, [videoType]); // Re-run when video type changes

  const checkAuthStatus = async () => {
    try {
      const { data } = await supabase.auth.getSession();
      
      if (data.session) {
        setIsAuthenticated(true);
        
        // Fetch user profile to get name
        const { data: profileData } = await supabase
          .from('profile')
          .select('name')
          .eq('user_id', data.session.user.id)
          .single();
        
        if (profileData && profileData.name) {
          let displayName = "";
          
          // Handle if name is stored as JSON
          if (typeof profileData.name === 'string') {
            try {
              if (profileData.name.startsWith('{') || profileData.name.startsWith('[')) {
                const parsed = JSON.parse(profileData.name);
                displayName = typeof parsed === 'string' ? parsed : String(parsed);
              } else {
                displayName = profileData.name;
              }
            } catch (e) {
              displayName = String(profileData.name);
            }
          } else if (profileData.name) {
            displayName = String(profileData.name);
          }
          
          setUserName(displayName);
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  };

  const loadVideos = async () => {
    try {
      setIsLoading(true);
      console.log(`Fetching ${videoType} videos for video wall...`);
      
      // Load different video types based on the selected filter
      let videosData;
      if (videoType === 'merchant') {
        videosData = await getMerchantVideos();
      } else {
        videosData = await getUserVideos();
      }
      
      if (videosData && Array.isArray(videosData)) {
        console.log(`Loaded ${videosData.length} ${videoType} videos for video wall`);
        setVideos(videosData);
      } else {
        console.error(`${videoType} videos data is not in expected format:`, videosData);
        toast.error("Error loading videos. Please try again.");
      }
    } catch (error) {
      console.error('Error loading videos:', error);
      toast.error("Failed to load videos. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecordClick = () => {
    navigate('/record');
  };

  const handleRefresh = () => {
    loadVideos();
    toast.info("Refreshing videos...");
  };

  const toggleVideoType = (type: 'merchant' | 'user') => {
    if (type !== videoType) {
      setVideoType(type);
      // Videos will reload due to the useEffect dependency
    }
  };

  return (    
    <div className="min-h-screen min-w-full overflow-hidden bg-black p-0 m-0">
      <div className="p-0 w-full h-screen">
        <div className="absolute top-20 right-4 z-10 flex flex-col gap-2">
          {/* Video type toggle buttons */}
          <div className="flex gap-2 mb-2">
            <Button 
              onClick={() => toggleVideoType('merchant')}
              variant={videoType === 'merchant' ? 'default' : 'outline'}
              className={videoType === 'merchant' 
                ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                : "bg-black/50 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"}
            >
              <Store className="h-4 w-4 mr-2" />
              Merchant Videos
            </Button>
            <Button 
              onClick={() => toggleVideoType('user')}
              variant={videoType === 'user' ? 'default' : 'outline'}
              className={videoType === 'user'
                ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                : "bg-black/50 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"}
            >
              <User className="h-4 w-4 mr-2" />
              User Videos
            </Button>
          </div>
          
          <Button 
            onClick={handleRefresh}
            variant="outline"
            className="bg-black/50 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black flex items-center"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Videos
          </Button>
          
          {isAuthenticated && (
            <Button 
              onClick={handleRecordClick}
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold flex items-center"
            >
              <Video className="h-4 w-4 mr-2" />
              Record Video
            </Button>
          )}
        </div>
         
        <div className="mt-2">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-700"></div>
            </div>
          ) : videos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-gray-400 mb-4">
                {videoType === 'merchant' 
                  ? "No merchant videos available yet." 
                  : "No user videos available yet."}
              </p>
              {isAuthenticated && (
                <Button 
                  onClick={handleRecordClick}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold"
                >
                  Be the first to record!
                </Button>
              )}
            </div>
          ) : (
            <div className="w-full h-full">
              <VideoWall videos={videos} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
